import React, { useMemo } from "react";
import { euro } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid } from "recharts";
import { BarChart3 } from "lucide-react";
import { MoreInfo } from "@/components/MoreInfo";

const eur = (n) => euro(n);
const PAID = ["accreditata", "pagata"];

function buildMonths(rows) {
  const now = new Date();
  const months = [];
  for (let i = 11; i >= 0; i -= 1) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    months.push({ key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`, label: d.toLocaleDateString("it-IT", { month: "short", year: "2-digit" }), maturate: 0, accreditate: 0 });
  }
  const idx = Object.fromEntries(months.map((m, i) => [m.key, i]));
  rows.forEach((r) => {
    const mk = (r.created_at || "").slice(0, 7);
    if (mk in idx && !["stornata", "da_recuperare", "in_arrivo", "annullata"].includes(r.status)) months[idx[mk]].maturate += Number(r.net || 0);
    if (PAID.includes(r.status)) {
      const pk = (r.pagata_at || r.accreditata_at || r.created_at || "").slice(0, 7);
      if (pk in idx) months[idx[pk]].accreditate += Number(r.net || 0);
    }
  });
  return months.map((m) => ({ ...m, maturate: Math.round(m.maturate * 100) / 100, accreditate: Math.round(m.accreditate * 100) / 100 }));
}

export function CommissionChart({ rows }) {
  const data = useMemo(() => buildMonths(rows), [rows]);
  const tot = data.reduce((s, m) => ({ mat: s.mat + m.maturate, acc: s.acc + m.accreditate }), { mat: 0, acc: 0 });
  return (
    <Card className="p-5 glow-border space-y-3" data-testid="commission-chart">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="font-medium flex items-center gap-2"><BarChart3 className="w-4 h-4 text-primary" /> Andamento ultimi 12 mesi</p>
        <p className="text-xs text-muted-foreground" data-testid="commission-chart-totals">Maturate {eur(tot.mat)} · accreditate/pagate {eur(tot.acc)}</p>
      </div>
      <div className="h-56">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
            <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} tickFormatter={(v) => euro(v, { maximumFractionDigits: 0 })} width={72} />
            <Tooltip formatter={(v) => eur(v)} contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar dataKey="maturate" name="Maturate (netto)" fill="#3b82f6" radius={[4, 4, 0, 0]} />
            <Bar dataKey="accreditate" name="Accreditate / pagate" fill="#14b8a6" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <MoreInfo title="Cosa indicano le barre" testid="commission-chart-info"><p className="text-xs text-muted-foreground">Maturate: contratti attivati nel mese. Accreditate/pagate: arrivate sul wallet o pagate dopo la fattura.</p></MoreInfo>
    </Card>
  );
}
